<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useRouter } from 'vue-router'

import EmailField from "@/components/authentication/fields/EmailField.vue";
import InputText from "@/components/common/fields/InputText.vue";
import { fetchPersonByCpf } from '@/services/api/integration.js'
import {blockLetters, formatCpfCnpj, formatPhone} from "@/utils/index.js";
import {useCustomerStore} from "@/stores/checkinStore.js";

const router = useRouter()

const cpfCnpj = ref('')
const name = ref('')
const email = ref('')
const phone = ref('')

const showExtraFields = ref(false)
const notFound = ref(false)
const errorMessage = ref('')
const isFullscreen = ref(false)

const toggleFullscreen = async () => {
    if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen()
        isFullscreen.value = true
    } else {
        await document.exitFullscreen()
        isFullscreen.value = false
    }
}

const handleLogout = () => {
    // `clear()` de verdade, e nao o `limparSessao()` usado no logout normal:
    // num totem o dispositivo e compartilhado, e lembra-lo como seguro e
    // justamente o que nao se quer.
    localStorage.clear()
    router.push('/login')
}


watch(cpfCnpj, () => {
    notFound.value = false
    showExtraFields.value = false
    errorMessage.value = ''
})

const formRef = ref(null)

const loading = ref(false)
const store = useCustomerStore()

const handleContinue = async () => {
    const digits = cpfCnpj.value.replace(/\D/g, '');

    if (digits.length < 11) {
        errorMessage.value = 'Digite um CPF ou CNPJ válido';
        return;
    }

    loading.value = true;
    errorMessage.value = '';

    try {
        const { data } = await fetchPersonByCpf(digits);

        if (data?.success) {
            setCustomer({
                name: data.response?.name,
                phone: data.response?.cellPhone,
                email: data.response?.email
            });
            router.push('/choose');
            return;
        }

        await handleNotFoundFlow();

    } catch (error) {
        console.error('Erro ao consultar CPF:', error);

        if (error.response?.status === 404) {
            await handleNotFoundFlow();
            return;
        }

        errorMessage.value = 'Não foi possível consultar o CPF agora. Tente novamente.';
    } finally {
        loading.value = false;
    }
};

const setCustomer = ({ name, phone, email }) => {
    store.customer = {
        customer_name: name,
        customer_identity: {
            phone,
            email
        }
    };
};

const handleNotFoundFlow = async () => {
    notFound.value = true;
    showExtraFields.value = true;

    const { valid } = await formRef.value.validate();
    if (!valid) return;

    setCustomer({
        name: name.value,
        phone: phone.value,
        email: email.value
    });

    router.push('/choose');
};

onMounted(() => {
    store.clearCustomer();
    document.addEventListener('fullscreenchange', () => {
        isFullscreen.value = !!document.fullscreenElement
    })
})


</script>

<template>
    <header class="header ga-2 d-flex align-center justify-space-between">
        <div class="d-flex align-center ga-2">
        </div>
        <div class="d-flex ga-2">
            <!-- BOTÃO LOGOUT (some em fullscreen) -->
            <v-btn
                v-if="!isFullscreen"
                icon
                variant="text"
                @click="handleLogout"
            >
                <i class="mdi mdi-logout"></i>
            </v-btn>

            <!-- BOTÃO FULLSCREEN -->
            <v-btn
                icon
                variant="text"
                @click="toggleFullscreen"
            >
                <i class="mdi" :class="isFullscreen ? 'mdi-fullscreen-exit' : 'mdi-fullscreen'"></i>
            </v-btn>
        </div>
    </header>

    <div class="container">

        <header class="header ga-2">
            <i class="mdi mdi-store icon-teal"></i>
            <span class="logo">CHECK-IN KIOSK</span>
        </header>

        <div class="card pb-30 px-5">

            <div class="card-title">
                <v-avatar size="40" variant="outlined" class="avatar-soft">
                    <i class="mdi mdi-account icon-teal"></i>
                </v-avatar>
                <h4>Identificação</h4>
            </div>
            <v-form ref="formRef">

                <label class="label-kiosk">CPF ou CNPJ *</label>
                <InputText
                    variant="outlined"
                    :fieldDefault="false"
                    v-model="cpfCnpj"
                    inputmode="numeric"
                    maxlength="18"
                    placeholder="000.000.000-00"
                    @keypress="blockLetters"
                    @update:modelValue="val => cpfCnpj = formatCpfCnpj(val)"
                    required
                />

                <transition name="fade">
                    <v-alert
                        v-if="notFound"
                        type="info"
                        variant="tonal"
                        rounded="lg"
                        class="mt-4"
                    >
                        CPF ou CNPJ não localizado. Preencha os dados abaixo para continuar.
                    </v-alert>
                </transition>

                <transition name="expand">
                    <div v-if="showExtraFields" class="expand-area mt-3">

                        <label class="label-kiosk">Nome *</label>
                        <InputText
                            variant="outlined"
                            :fieldDefault="false"
                            v-model="name"
                            placeholder="Digite seu nome"
                            required
                        />

                        <label class="label-kiosk">Celular *</label>
                        <InputText
                            variant="outlined"
                            :fieldDefault="false"
                            v-model="phone"
                            maxlength="15"
                            placeholder="(00) 00000-0000"
                            @keypress="blockLetters"
                            @update:modelValue="val => phone = formatPhone(val)"
                            required
                        />

                        <label class="label-kiosk">E-mail</label>
                        <EmailField
                            variant="outlined"
                            :fieldDefault="false"
                            v-model="email"
                            placeholder="exemplo@email.com"
                        />

                    </div>
                </transition>

                <v-row class="d-flex justify-center pt-0">
                    <v-col class="d-flex justify-content-center  pt-0" md="7">
                        <v-btn
                            block
                            rounded="xl"
                            size="large"
                            class="text-none mt-5 d-flex justify-center"
                            style="background-color:#1f7a8c;color:white;"
                            :loading="loading"
                            @click="handleContinue"
                        >
                            Continuar
                            <i class="material-symbols-outlined ml-2">arrow_forward</i>
                        </v-btn>
                    </v-col>
                </v-row>

            </v-form>

        </div>
    </div>
</template>


<style scoped>
.label-kiosk {
    font-size:15px;
    font-weight:bold;
}
.material-symbols-rounded {
    font-variation-settings: 'FILL' 1;
}
.fade-enter-active, .fade-leave-active {
    transition: opacity .25s ease;
}
.fade-enter-from, .fade-leave-to {
    opacity: 0;
}

.expand-enter-active {
    transition: all .35s ease;
}
.expand-enter-from {
    max-height: 0;
    opacity: 0;
    transform: translateY(-6px);
}
.expand-enter-to {
    max-height: 500px;
    opacity: 1;
    transform: translateY(0);
}

.avatar-soft {
    background-color: #f1f5f9; /* cinza suave moderno */
    border: 1px solid #e2e8f0;
}

.icon-teal {
    color: #1f7a8c; /* tom azul petróleo similar ao botão */
    font-size: 26px;
}

.header {
    display: flex;
    align-items: center;
    margin-bottom: 16px;
}

.logo {
    font-weight: 600;
    font-size: 14px;
    color: #64748b;
}

.close {
    border: none;
    background: #e2e8f0;
    border-radius: 50%;
    width: 34px;
    height: 34px;
    cursor: pointer;
}

h2 {
    font-size: 22px;
    margin-bottom: 6px;
}

.subtitle {
    font-size: 14px;
    color: #64748b;
    margin-bottom: 20px;
}

.card {
    background: white;
    border-radius: 16px;
    padding: 16px;
    box-shadow: 0 6px 20px rgba(0,0,0,0.06);
    margin-bottom: 16px;
}

.card-title {
    font-weight: 600;
    margin-bottom: 12px;
    display: flex;
    align-items: center;
    gap: 8px;
}

label {
    display: block;
    font-size: 13px;
    margin-top: 10px;
    margin-bottom: 4px;
    color: #475569;
}

input {
    width: 100%;
    padding: 10px 12px;
    border-radius: 10px;
    border: 1px solid #e2e8f0;
    font-size: 14px;
    outline: none;
}

input:focus {
    border-color: #0ea5e9;
}

.btn {
    width: 100%;
    margin-top: 14px;
    padding: 12px;
    border-radius: 999px;
    border: none;
    font-weight: 600;
    cursor: pointer;
}

.primary {
    background: #0ea5e9;
    color: white;
}

.secondary {
    background: #e2f3ff;
    color: #0369a1;
}

.skip {
    margin-top: 20px;
    width: 100%;
    border: none;
    background: none;
    color: #64748b;
    font-size: 14px;
    cursor: pointer;
}
</style>
